import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CorporateProfile, PronounType } from "@/types/database/corporate";
import { ISD_CODES } from "@/lib/constants";
import { Upload, Loader2, ChevronsUpDown, Check, Users, Target } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface CorporateSignupFormProps {
  onSubmitProp?: (data: CorporateProfile) => Promise<void>;
  session: any;
  profile: any;
}

interface CorporateProfileFormData extends CorporateProfile {
  profile_picture?: File;
}

type EnumData = {
  data: {
    sdg: Array<{
      id: string
      value: string
    }>
    opportunityType: string[]
    opportunityBeneficiaryType: string[]
    causeAreas: string[]
  }
}

interface ApiResponse<T> {
  data: T;
  message: string;
}

const PRONOUNS: PronounType[] = ["He/Him", "She/Her", "They/Them", "Ze/Zir", "Xe/Xem", "Other", "Prefer not to say"];

export const CorporateSignupForm = ({ onSubmitProp, session, profile }: CorporateSignupFormProps) => {
  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm<CorporateProfileFormData>({
    defaultValues: {
      id: "",
      first_name: "",
      last_name: "",
      company_email: "",
      isd_code: "",
      phone: "",
      pronouns: undefined,
      title: "",
      cause_areas: [],
      beneficiary_types: [],
      company_name: "",
      profile_photo_url: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }
  });

  const [loading, setLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedCauseAreas, setSelectedCauseAreas] = useState<string[]>([]);
  const [selectedBeneficiaries, setSelectedBeneficiaries] = useState<string[]>([]);
  const [openCauseAreas, setOpenCauseAreas] = useState(false);
  const [openBeneficiary, setOpenBeneficiary] = useState(false);
  const { toast } = useToast();

  // Fetch enums
  const { data: enumData, isLoading: isLoadingEnums, error: enumError } = useQuery<EnumData>({
    queryKey: ["enums"],
    queryFn: async () => {
      const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/opportunities/enums`, {
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        },
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || "Failed to fetch enums");
      }
      return response.json();
    },
    enabled: !!session?.access_token,
    retry: 2
  });

  // Fetch corporate profile
  const { data: profileResponse, isLoading: isLoadingProfile, error: profileError } = useQuery<ApiResponse<CorporateProfile>>({
    queryKey: ["corporateProfile"],
    queryFn: async () => {
      const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/me/profile/corporate`, {
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        },
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || "Failed to fetch profile");
      }
      return response.json();
    },
    enabled: !!session?.access_token,
    retry: 2
  });

  // Handle errors from queries
  useEffect(() => {
    if (enumError) {
      toast({
        title: "Error",
        description: enumError instanceof Error ? enumError.message : "Failed to fetch enums",
        variant: "destructive",
      });
    }
  }, [enumError, toast]);

  useEffect(() => {
    if (profileError) {
      toast({
        title: "Error",
        description: profileError instanceof Error ? profileError.message : "Failed to fetch profile",
        variant: "destructive",
      });
    }
  }, [profileError, toast]);

  useEffect(() => {
    if (profileResponse?.data) {
      const profile = profileResponse.data as any;
      setValue("first_name", profile.poc_first_name);
      setValue("last_name", profile.poc_last_name);
      setValue("company_email", profile.email);
      setValue("isd_code", profile.isd_code);
      setValue("phone", profile.phone);
      setValue("pronouns", profile.pronouns);
      setValue("title", profile.title);
      setValue("company_name", profile.org_name);
      setValue("profile_photo_url", profile.profile_photo_url);
      
      if (profile.meta?.cause_areas) {
        setSelectedCauseAreas(profile.meta.cause_areas);
        setValue("cause_areas", profile.meta.cause_areas);
      }
      
      if (profile.meta?.beneficiary_types) {
        setSelectedBeneficiaries(profile.meta.beneficiary_types);
        setValue("beneficiary_types", profile.meta.beneficiary_types);
      }
    }
  }, [profileResponse, setValue]);

  const currentPronouns = watch("pronouns");
  const currentIsdCode = watch("isd_code");

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setValue("profile_picture", file);
    }
  };

  const onSubmit = async (data: CorporateProfileFormData) => {
    if (!session) {
      toast({
        title: "Error",
        description: "You must be logged in to update your profile",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);
      
      let profilePhotoUrl = data.profile_photo_url;

      if (data.profile_picture) {
        const file = data.profile_picture;
        const fileExt = file.name.split('.').pop();
        const filePath = `${session.user.id}/profile/profile-photo.${fileExt}`;

        await supabase.auth.setSession({
          access_token: session.access_token,
          refresh_token: session.refresh_token,
        });
        const { error: uploadError, data: uploadData } = await supabase.storage
          .from('nonprofit-cloud')
          .upload(filePath, file, { upsert: true });

        if (uploadError) {
          throw new Error(`Error uploading file: ${uploadError.message}`);
        }

        const { data: { publicUrl } } = supabase.storage
          .from('nonprofit-cloud')
          .getPublicUrl(filePath);

        profilePhotoUrl = publicUrl;
      }

      const formData = {
        poc_first_name: data.first_name,
        poc_last_name: data.last_name,
        email: data.company_email,
        isd_code: data.isd_code,
        phone: data.phone,
        pronouns: data.pronouns,
        title: data.title,
        meta: {
          cause_areas: selectedCauseAreas,
          beneficiary_types: selectedBeneficiaries,
        },
        org_name: data.company_name,
        profile_photo_url: profilePhotoUrl,
      };

      const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/me/profile/corporate`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to update profile');
      }

      toast({
        title: "Success",
        description: "Profile updated successfully",
      });

      if (onSubmitProp) {
        await onSubmitProp(formData as any);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update profile",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (isLoadingProfile || isLoadingEnums) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (enumError || profileError) {
    return (
      <div className="flex items-center justify-center p-8">
        <p className="text-lg text-red-500">Error loading data. Please try again later.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="first_name">First Name</Label>
          <Input
            id="first_name"
            {...register("first_name", { required: "First name is required" })}
            className={errors.first_name ? "border-red-500" : ""}
          />
          {errors.first_name && (
            <p className="text-sm text-red-500 mt-1">{errors.first_name.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="last_name">Last Name</Label>
          <Input
            id="last_name"
            {...register("last_name", { required: "Last name is required" })}
            className={errors.last_name ? "border-red-500" : ""}
          />
          {errors.last_name && (
            <p className="text-sm text-red-500 mt-1">{errors.last_name.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="company_name">Company Name</Label>
          <Input
            id="company_name"
            {...register("company_name", { required: "Company name is required" })}
            className={errors.company_name ? "border-red-500" : ""}
          />
          {errors.company_name && (
            <p className="text-sm text-red-500 mt-1">{errors.company_name.message}</p>
          )}
        </div>

        <div>
          <Label className="flex items-center gap-2">
            <Upload className="h-4 w-4" />
            Profile Photo
          </Label>
          <div className="flex items-center gap-4">
            <div className="h-[80px] w-[80px] flex-shrink-0 relative bg-gray-50 rounded-lg flex items-center justify-center">
              {profileResponse?.data?.profile_photo_url ? (
                <img 
                  src={profileResponse.data.profile_photo_url} 
                  alt="Profile Photo" 
                  className="max-h-full max-w-full h-auto w-auto object-contain p-2"
                />
              ) : (
                <div className="text-gray-400 text-sm">No logo</div>
              )}
            </div>
            <div className="flex-1">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*"
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
              >
                {isUploading ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Uploading...</>
                ) : (
                  <><Upload className="h-4 w-4 mr-2" /> Upload Photo</>
                )}
              </Button>
              <p className="text-xs text-gray-500 mt-1">
                Upload a profile photo (max 5MB)
              </p>
            </div>
          </div>
        </div>

        <div>
          <Label htmlFor="title">Job Title</Label>
          <Input
            id="title"
            {...register("title", { required: "Job title is required" })}
            className={errors.title ? "border-red-500" : ""}
          />
          {errors.title && (
            <p className="text-sm text-red-500 mt-1">{errors.title.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="company_email">Company Email</Label>
          <Input
            id="company_email"
            type="email"
            {...register("company_email", {
              required: "Company email is required",
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: "Invalid email address"
              }
            })}
            className={errors.company_email ? "border-red-500" : ""}
          />
          {errors.company_email && (
            <p className="text-sm text-red-500 mt-1">{errors.company_email.message}</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="isd_code">ISD Code</Label>
            <Select
              value={currentIsdCode}
              onValueChange={(value) => setValue("isd_code", value)}
            >
              <SelectTrigger className={errors.isd_code ? "border-red-500" : ""}>
                <SelectValue placeholder="Select ISD code" />
              </SelectTrigger>
              <SelectContent>
                {ISD_CODES.map((code) => (
                  <SelectItem key={code.value} value={code.value}>
                    {code.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.isd_code && (
              <p className="text-sm text-red-500 mt-1">{errors.isd_code.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="phone">Phone Number</Label>
            <Input
              id="phone"
              type="tel"
              {...register("phone", {
                required: "Phone number is required",
              })}
              className={errors.phone ? "border-red-500" : ""}
            />
            {errors.phone && (
              <p className="text-sm text-red-500 mt-1">{errors.phone.message}</p>
            )}
          </div>
        </div>

        <div>
          <Label htmlFor="pronouns">Pronouns</Label>
          <Select
            value={currentPronouns}
            onValueChange={(value) => setValue("pronouns", value as PronounType)}
          >
            <SelectTrigger className={errors.pronouns ? "border-red-500" : ""}>
              <SelectValue placeholder="Select pronouns" />
            </SelectTrigger>
            <SelectContent>
              {PRONOUNS.map((pronoun) => (
                <SelectItem key={pronoun} value={pronoun}>
                  {pronoun}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.pronouns && (
            <p className="text-sm text-red-500 mt-1">{errors.pronouns.message}</p>
          )}
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Target className="h-4 w-4" />
              Cause Areas
            </Label>
            <div className="space-y-2">
              <Popover open={openCauseAreas} onOpenChange={setOpenCauseAreas}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={openCauseAreas}
                    className="w-full justify-between"
                  >
                    {selectedCauseAreas?.length
                      ? `${selectedCauseAreas.length} selected`
                      : "Select cause areas..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0">
                  <Command>
                    <CommandInput placeholder="Search cause areas..." />
                    <CommandList>
                      <CommandEmpty>No results found.</CommandEmpty>
                      <CommandGroup>
                        {enumData?.data?.opportunityType?.map((area) => {
                          const isSelected = selectedCauseAreas.includes(area);
                          return (
                            <CommandItem
                              key={area}
                              onSelect={() => {
                                const newSelection = selectedCauseAreas.includes(area)
                                  ? selectedCauseAreas.filter(a => a !== area)
                                  : [...selectedCauseAreas, area];
                                setSelectedCauseAreas(newSelection);
                                setValue("cause_areas", newSelection);
                              }}
                            >
                              <div
                                className={cn(
                                  "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                                  isSelected
                                    ? "bg-primary text-primary-foreground"
                                    : "opacity-50 [&_svg]:invisible"
                                )}
                              >
                                <Check className="h-4 w-4" />
                              </div>
                              <span className="text-sm">{area}</span>
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {selectedCauseAreas?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {selectedCauseAreas.map((area) => (
                    <Badge key={area} variant="secondary">
                      {area}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Beneficiary Types
            </Label>
            <div className="space-y-2">
              <Popover open={openBeneficiary} onOpenChange={setOpenBeneficiary}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={openBeneficiary}
                    className="w-full justify-between"
                  >
                    {selectedBeneficiaries?.length
                      ? `${selectedBeneficiaries.length} selected`
                      : "Select beneficiary types..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0">
                  <Command>
                    <CommandInput placeholder="Search beneficiary types..." />
                    <CommandList>
                      <CommandEmpty>No results found.</CommandEmpty>
                      <CommandGroup>
                        {enumData?.data?.opportunityBeneficiaryType?.map((type) => {
                          const isSelected = selectedBeneficiaries.includes(type);
                          return (
                            <CommandItem
                              key={type}
                              onSelect={() => {
                                const newSelection = selectedBeneficiaries.includes(type)
                                  ? selectedBeneficiaries.filter(t => t !== type)
                                  : [...selectedBeneficiaries, type];
                                setSelectedBeneficiaries(newSelection);
                                setValue("beneficiary_types", newSelection);
                              }}
                            >
                              <div
                                className={cn(
                                  "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                                  isSelected
                                    ? "bg-primary text-primary-foreground"
                                    : "opacity-50 [&_svg]:invisible"
                                )}
                              >
                                <Check className="h-4 w-4" />
                              </div>
                              <span className="text-sm">{type}</span>
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {selectedBeneficiaries?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {selectedBeneficiaries.map((type) => (
                    <Badge key={type} variant="secondary">
                      {type}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <Button type="submit" className="w-full" disabled={loading}>
          {loading ? "Updating..." : "Complete Profile"}
        </Button>
      </div>
    </form>
  );
};